import {
  Component,
  OnInit,
  computed,
  signal,
  inject,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { injectQuery } from '@tanstack/angular-query-experimental';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { CustomerService } from '@kanzi-apes/kanzi-customer';
import * as fromAuth from '@kanzi-apes/kanzi-auth';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { LocationCreateModel, OperatingHours, CountryFilterModel, StateCountryFilterModel, CityFilterModel, CustomerListModel } from '@kanzi-apes/kanzi-models';
import { Store } from '@ngrx/store';
import { StoresQueriesService, UbicacionesQueriesService } from '../../../queries';
import { OperationScheduleComponent } from '../../../components/operation-schedule/operation-schedule.component';

@Component({
    selector: 'kanzi-apes-store-edit',
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        InputTextareaModule,
        DropdownModule,
        MultiSelectModule,
        SkeletonModule,
        ToastModule,
        KanziUiModule,
        OperationScheduleComponent,
    ],
    providers: [MessageService],
    templateUrl: './store-edit.component.html',
    styleUrls: ['./store-edit.component.scss']
})
export class StoreEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly queries = inject(StoresQueriesService);
  private readonly locationQueries = inject(UbicacionesQueriesService);
  private readonly messageService = inject(MessageService);
  private readonly ngrxStore = inject(Store);
  private readonly customerService = inject(CustomerService);
  userLogged = this.ngrxStore.selectSignal(fromAuth.getAuthUser);
  customerSelected = this.ngrxStore.selectSignal(fromCustomer.getCustomerSelected);

  // Determines if customer multi-select should be shown
  showCustomerSelector = computed(
    () => this.userLogged()?.configs?.customer?.parent == null
  );

  // Query to load available customers (enabled only when multi-select is needed)
  customersQuery = injectQuery<CustomerListModel, Error>(() => ({
    queryKey: ['customers', { page_size: 1000 }],
    queryFn: () =>
      lastValueFrom(this.customerService.listCustomers({ page_size: 1000 })),
    enabled: this.showCustomerSelector(),
    staleTime: 1000 * 60 * 5,
  }));

  // Selected customer IDs for multi-select (plain property for ngModel compatibility)
  selectedCustomerIds: number[] = [];

  // Signals
  storeId = signal<number | null>(null);
  isQueryEnabled = signal(false);

  // Store detail query
  storeQuery = this.queries.useStoreDetail(this.storeId, this.isQueryEnabled);

  // Form data (mutable for ngModel)
  store: LocationCreateModel = {
    name: '',
    display_name: '',
    external_id: '',
    description: '',
    customer_ids: [],
    parent_id: 0,
    type_id: undefined,
    country_id: undefined,
    state_id: undefined,
    city_id: undefined,
    address: '',
    postal_code: '',
    phone: '',
    mobile: '',
    operating_hours: undefined,
  };

  // Validation errors
  phoneError = '';
  mobileError = '';
  postalCodeError = '';

  // Computed to check if loading
  isLoading = computed(
    () => this.storeQuery.isPending() || this.storeQuery.isFetching()
  );

  // Geographic queries
  countriesFilters = signal<CountryFilterModel | null>({ page_size: 1000 });
  countriesQuery = this.locationQueries.useCountries(this.countriesFilters);

  statesFilters = signal<StateCountryFilterModel | null>(null);
  statesQuery = this.locationQueries.useStates(this.statesFilters);

  citiesFilters = signal<CityFilterModel | null>(null);
  citiesQuery = this.locationQueries.useCities(this.citiesFilters);

  // Mutation
  updateMutation = this.queries.useUpdateStore();

  constructor() {
    // Effect to load store data into form
    effect(() => {
      const data = this.storeQuery.data();
      if (data) {
        // Extract customer IDs from the location data
        const customerIds = data.customers?.map(c => c.id).filter((id): id is number => id !== undefined) || 
                           data.customer?.map(c => c.id).filter((id): id is number => id !== undefined) ||
                           (data.customer_id ? [data.customer_id] : []);

        // Extract geographic IDs
        const countryId = data.country?.id || data.country_id || undefined;
        const stateId = data.state?.id || data.state_id || undefined;
        const cityId = data.city?.id || data.city_id || undefined;

        this.store = {
          id: data.id,
          name: data.name || '',
          display_name: data.display_name || data.name || '',
          external_id: data.external_id !== undefined && data.external_id !== null
            ? String(data.external_id)
            : undefined,
          description: data.description || '',
          type_id: data.type?.id,
          customer_ids: customerIds,
          country_id: countryId,
          state_id: stateId,
          city_id: cityId,
          address: data.address || '',
          postal_code: data.postal_code || '',
          phone: data.phone || '',
          mobile: data.mobile || '',
          operating_hours: data.operating_hours || undefined,
        };

        // Pre-populate multi-select with existing customer IDs
        this.selectedCustomerIds = [...customerIds];

        // Load states if country is selected - use untracked to avoid triggering effect again
        if (countryId) {
          untracked(() => {
            this.statesFilters.set({
              country_id: countryId,
              page_size: 1000,
            });
          });
        }

        // Load cities if state is selected - use untracked to avoid triggering effect again
        if (stateId) {
          untracked(() => {
            this.citiesFilters.set({
              state_id: stateId,
              page_size: 1000,
            });
          });
        }
      }
    });
  }

  ngOnInit(): void {
    // Get ID from route
    this.route.params.subscribe((params) => {
      const id = Number(params['id']);
      if (id) {
        this.storeId.set(id);
        this.isQueryEnabled.set(true);
      }
    });
  }

  /**
   * Handle schedule changes from OperationScheduleComponent
   */
  onSchedulesChange(operatingHours: OperatingHours): void {
    this.store.operating_hours = operatingHours;
  }

  /**
   * Handle country selection change
   * Resets state and city when country changes
   */
  onCountryChange(): void {
    // Reset dependent fields
    this.store.state_id = undefined;
    this.store.city_id = undefined;

    // Update states filter
    if (this.store.country_id) {
      this.statesFilters.set({
        country_id: this.store.country_id,
        page_size: 1000,
      });
    } else {
      this.statesFilters.set(null);
    }
    
    // Reset cities filter
    this.citiesFilters.set(null);
  }

  /**
   * Handle state selection change
   * Resets city when state changes
   */
  onStateChange(): void {
    // Reset dependent field
    this.store.city_id = undefined;

    // Update cities filter
    if (this.store.state_id) {
      this.citiesFilters.set({
        state_id: this.store.state_id,
        page_size: 1000,
      });
    } else {
      this.citiesFilters.set(null);
    }
  }

  /**
   * Validate phone number format: digits, +, -, spaces only. Min 7, max 20 characters
   */
  validatePhone(): void {
    if (this.store.phone) {
      const phone = this.store.phone.trim();
      const phoneRegex = /^[\d\s\-+]+$/;
      
      if (!phoneRegex.test(phone)) {
        this.phoneError = 'El teléfono solo puede contener dígitos, +, -, y espacios';
      } else if (phone.length < 7 || phone.length > 20) {
        this.phoneError = 'El teléfono debe tener entre 7 y 20 caracteres';
      } else {
        this.phoneError = '';
      }
    } else {
      this.phoneError = '';
    }
  }

  /**
   * Validate mobile number format: digits, +, -, spaces only. Min 7, max 20 characters
   */
  validateMobile(): void {
    if (this.store.mobile) {
      const mobile = this.store.mobile.trim();
      const phoneRegex = /^[\d\s\-+]+$/;
      
      if (!phoneRegex.test(mobile)) {
        this.mobileError = 'El celular solo puede contener dígitos, +, -, y espacios';
      } else if (mobile.length < 7 || mobile.length > 20) {
        this.mobileError = 'El celular debe tener entre 7 y 20 caracteres';
      } else {
        this.mobileError = '';
      }
    } else {
      this.mobileError = '';
    }
  }

  /**
   * Validate postal code (3-10 alphanumeric characters)
   */
  validatePostalCode(): void {
    if (this.store.postal_code) {
      const postalCode = this.store.postal_code.trim();
      if (postalCode.length < 3 || postalCode.length > 10) {
        this.postalCodeError = 'El código postal debe tener entre 3 y 10 caracteres';
      } else if (!/^[a-zA-Z0-9]+$/.test(postalCode)) {
        this.postalCodeError = 'El código postal solo puede contener letras y números';
      } else {
        this.postalCodeError = '';
      }
    } else {
      this.postalCodeError = '';
    }
  }

  /**
   * Check if form has validation errors
   */
  hasValidationErrors(): boolean {
    this.validatePostalCode();
    return !!(this.phoneError || this.mobileError || this.postalCodeError);
  }

  /**
   * Save changes
   */
  onSave(): void {
    // Basic validation
    if (!this.store.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre del store es requerido',
      });
      return;
    }

    // Check for validation errors
    if (this.hasValidationErrors()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor corrija los errores en el formulario',
      });
      return;
    }

    if (!this.store.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'ID del store no encontrado',
      });
      return;
    }

    // Validate at least one customer is selected when multi-select is active
    if (this.showCustomerSelector() && this.selectedCustomerIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe seleccionar al menos un cliente',
      });
      return;
    }

    const dataToSave: LocationCreateModel = {
      ...this.store,
      customer_ids: this.showCustomerSelector()
        ? this.selectedCustomerIds
        : this.store.customer_ids,
    };

    this.updateMutation.mutate(dataToSave, {
      onSuccess: (data) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Store actualizado',
          detail: `El store "${data.name}" fue actualizado exitosamente`,
        });

        // Navigate to detail
        if (data.id) {
          this.router.navigate(['locations', 'stores', 'detail', data.id]);
        }
      },
      onError: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error al actualizar',
          detail: error.message || 'Ocurrió un error al actualizar el store',
        });
      },
    });
  }

  /**
   * Cancel and go back to detail
   */
  onCancel(): void {
    const id = this.storeId();
    if (id) {
      this.router.navigate(['locations', 'stores', 'detail', id]);
    } else {
      this.router.navigate(['locations', 'stores', 'list']);
    }
  }
}
